import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  homePath,
  loginPath,
  normalizeAlias,
  readLastAlias,
  withContractAlias,
} from "../utils/contractPath";

/**
 * Alias efectivo: param da URL → user da sessão → último alias gravado.
 */
export function useContractAlias() {
  const { cdAlias: paramAlias } = useParams();
  const { user } = useAuth();
  return useMemo(() => {
    return (
      normalizeAlias(paramAlias) ||
      normalizeAlias(user?.cd_alias) ||
      readLastAlias() ||
      ""
    );
  }, [paramAlias, user?.cd_alias]);
}

/** Prefixa paths internos com /{alias}. */
export function useContractPath() {
  const alias = useContractAlias();
  return useCallback((path) => withContractAlias(alias, path), [alias]);
}

export function useContractNavigate() {
  const navigate = useNavigate();
  const cp = useContractPath();
  return useCallback(
    (to, options) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      if (typeof to === "string") {
        // Relativos (., .., foo) sem prefixo; absolutos /… recebem alias.
        if (
          to === "." ||
          to === ".." ||
          to.startsWith("./") ||
          to.startsWith("../") ||
          !to.startsWith("/")
        ) {
          navigate(to, options);
          return;
        }
        navigate(cp(to), options);
        return;
      }
      if (to && typeof to === "object" && to.pathname != null) {
        const pn = String(to.pathname);
        const pathname =
          pn.startsWith("/") && !pn.startsWith("./") ? cp(pn) : pn;
        navigate({ ...to, pathname }, options);
        return;
      }
      navigate(to, options);
    },
    [navigate, cp]
  );
}

export function useLoginPath() {
  const alias = useContractAlias();
  return loginPath(alias);
}

export function useHomePath() {
  const alias = useContractAlias();
  return homePath(alias);
}
